
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { logger } from "@/services/LoggingService";

const COMPONENT = 'DocumentUploader';

interface DocumentUploaderProps {
  fundId: string;
  onUploadComplete: (newDoc: any) => void;
}

export function DocumentUploader({ fundId, onUploadComplete }: DocumentUploaderProps) {
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if ((files?.length || 0) === 0) return;

    setIsUploading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const file of files) {
        try {
          const fileExt = file.name.split('.').pop();
          const filePath = `${fundId}/${crypto.randomUUID()}.${fileExt}`;

          // Upload to storage first
          const { error: uploadError } = await supabase.storage
            .from('fund_documents')
            .upload(filePath, file);

          if (uploadError) {
            logger.error(COMPONENT, 'Storage upload error:', { error: uploadError, fileName: file.name });
            throw uploadError;
          }

          // If storage upload successful, create database record
          const { data: newDoc, error: dbError } = await supabase
            .from('fund_documents')
            .insert({
              fund_id: fundId,
              file_name: file.name,
              file_path: filePath,
              display_title: file.name,
            })
            .select()
            .single();

          if (dbError) {
            // If database insert fails, clean up the uploaded file
            await supabase.storage
              .from('fund_documents')
              .remove([filePath]);
            
            logger.error(COMPONENT, 'Database insert error:', { error: dbError, fileName: file.name });
            throw dbError;
          }

          if (newDoc) {
            onUploadComplete(newDoc);
            successCount++;
          }
        } catch (error) {
          errorCount++;
          logger.error(COMPONENT, 'Upload process error:', { error, fileName: file.name });
        }
      }

      if (successCount > 0) {
        toast({
          title: "Documents uploaded",
          description: `Successfully uploaded ${successCount} document${successCount > 1 ? 's' : ''}.${
            errorCount > 0 ? ` Failed to upload ${errorCount} file${errorCount > 1 ? 's' : ''}.` : ''
          }`,
        });
      } else {
        toast({
          title: "Upload failed",
          description: "There was an error uploading the documents.",
          variant: "destructive",
        });
      }
    } catch (error) {
      logger.error(COMPONENT, 'Unexpected upload error:', { error });
      toast({
        title: "Upload failed",
        description: "An unexpected error occurred during upload.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  return (
    <div>
      <input
        type="file"
        accept=".pdf"
        multiple
        onChange={handleFileUpload}
        className="hidden"
        id="document-upload"
        disabled={isUploading}
      />
      <label htmlFor="document-upload">
        <Button variant="outline" asChild disabled={isUploading}>
          <span>
            {isUploading ? (
              <>
                <Spinner size="sm" className="mr-2" ariaLabel="Uploading" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload Documents
              </>
            )}
          </span>
        </Button>
      </label>
    </div>
  );
}
